import React, { useEffect, useId, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X } from "lucide-react";

/*
 * ExpandableCard — a card whose icon acts as the "cover"; clicking morphs the
 * card into a centered dialog (shared-layout animation) showing the icon, title,
 * body and bullets. Adapted to JS + framer-motion and reskinned to the Nexxis
 * brand from a 21st.dev linear/dialog card. Icon stands in for the image.
 */
const SPRING = { type: "spring", bounce: 0.05, duration: 0.5 };

export function ExpandableCard({ icon: Icon, num, title, body, bullets = [], testId }) {
  const uid = useId();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.classList.add("overflow-hidden");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("overflow-hidden");
    };
  }, [open]);

  const IconPanel = ({ size }) => {
    const big = size > 200;
    const iconSize = big ? 88 : 64;
    return (
      <div
        className="relative flex items-center justify-center overflow-hidden"
        style={{ background: "linear-gradient(150deg, #0D5F5E, #0B4F4E)", height: size }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#BEE5E012_1px,transparent_1px),linear-gradient(to_bottom,#BEE5E012_1px,transparent_1px)] bg-[size:30px_30px]" />
        {/* soft halo so the icon reads as the focal element */}
        <div
          className="pointer-events-none absolute rounded-full"
          style={{
            width: iconSize * 2.6,
            height: iconSize * 2.6,
            background: "radial-gradient(circle, rgba(190,229,224,0.22), transparent 65%)",
          }}
        />
        {Icon ? (
          <span className="relative transition-transform duration-300 ease-out group-hover:scale-110">
            <Icon size={iconSize} strokeWidth={big ? 1.7 : 2} style={{ color: "#E8F6F3" }} />
          </span>
        ) : null}
        {num ? (
          <span
            className="font-display absolute right-4 top-3"
            style={{ fontSize: 20, color: "rgba(190,229,224,0.85)" }}
          >
            {num}<span style={{ color: "#FF6857" }}>.</span>
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {/* Trigger card */}
      <motion.div
        layoutId={`exp-card-${uid}`}
        data-testid={testId}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="nxx-flip-surface group relative h-full cursor-pointer overflow-hidden rounded-[14px]"
        style={{ border: "1px solid rgba(190,229,224,0.18)" }}
      >
        <motion.div layoutId={`exp-icon-${uid}`}>
          <IconPanel size={150} />
        </motion.div>
        <div className="flex items-center justify-between p-5">
          <motion.h3
            layoutId={`exp-title-${uid}`}
            className="font-display"
            style={{ fontSize: 22, color: "#F7F6F2", fontWeight: 500, letterSpacing: "-0.01em" }}
          >
            {title}
          </motion.h3>
          <span
            className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-transform duration-300 ease-out group-hover:scale-110"
            style={{ background: "#FF6857", color: "#F7F6F2", boxShadow: "0 6px 16px rgba(255,104,87,0.35)" }}
          >
            <Plus size={18} strokeWidth={2.2} />
          </span>
        </div>
      </motion.div>

      {/* Expanded dialog */}
      <AnimatePresence initial={false}>
        {open && (
          <>
            <motion.div
              key={`backdrop-${uid}`}
              className="fixed inset-0 z-[80] backdrop-blur-sm"
              style={{ background: "rgba(6,32,31,0.5)" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <div className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto p-5 pt-24 sm:pt-28">
              <motion.div
                layoutId={`exp-card-${uid}`}
                role="dialog"
                aria-modal="true"
                className="nxx-flip-surface relative w-full max-w-[720px] overflow-hidden rounded-[22px]"
                style={{ border: "1px solid rgba(190,229,224,0.22)" }}
              >
                <motion.div layoutId={`exp-icon-${uid}`}>
                  <IconPanel size={220} />
                </motion.div>

                <div className="p-7 sm:p-9">
                  <motion.h3
                    layoutId={`exp-title-${uid}`}
                    className="font-display"
                    style={{
                      fontSize: "clamp(30px, 4vw, 44px)",
                      lineHeight: 1.05,
                      color: "#F7F6F2",
                      fontWeight: 500,
                      letterSpacing: "-0.015em",
                    }}
                  >
                    {title}
                  </motion.h3>

                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 12 }}
                    transition={{ duration: 0.25, delay: 0.06 }}
                  >
                    <div className="mt-4 h-px" style={{ background: "#FF6857", width: 40 }} />
                    <p
                      className="font-ui mt-5"
                      style={{ color: "rgba(247,246,242,0.85)", lineHeight: 1.65, fontSize: 15.5 }}
                    >
                      {body}
                    </p>
                    {bullets.length > 0 && (
                      <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                        {bullets.map((b) => (
                          <li
                            key={b}
                            className="flex items-center gap-3 font-ui text-[13.5px]"
                            style={{ color: "rgba(247,246,242,0.88)" }}
                          >
                            <span className="h-px w-4 shrink-0" style={{ background: "#BEE5E0" }} />
                            {b}
                          </li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                </div>

                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                  className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors"
                  style={{ background: "rgba(6,32,31,0.45)", color: "#F7F6F2", backdropFilter: "blur(6px)" }}
                >
                  <X size={20} />
                </button>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
